import React from 'react';

import { Flex, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { formatDate, formatDateAndTimeShort, fromUtc } from '@app/services/date';

import { formatDecimal, getRowModules } from '../../data/cargo';
import { ALL_TAB, COUNTERPARTY_ORDERS_PATH, CounterpartyOrderRow, OrdersTab } from '../../interfaces';
import { DeliveryAddress } from '../DeliveryAddress';
import { ModuleIcon } from '../ModuleIcon';
import { OrderStatusTag } from '../OrderStatusTag';
import { Places } from '../Places';

const CARGO_COLUMNS = 3;
const cargoSpan = (row: CounterpartyOrderRow) =>
  row.cargo_unavailable ? { colSpan: CARGO_COLUMNS, style: { textAlign: 'center' as const } } : {};
const coveredByCargoSpan = (row: CounterpartyOrderRow) => (row.cargo_unavailable ? { colSpan: 0 } : {});

interface IProps {
  tab: OrdersTab;
  rows: CounterpartyOrderRow[];
  total?: number;
  loading: boolean;
  current: number;
  pageSize: number;
  onPaginationChange: (current: number, pageSize: number) => void;
  empty: React.ReactNode;
}

export const OrdersTable: React.FC<IProps> = ({
  tab,
  rows,
  total,
  loading,
  current,
  pageSize,
  onPaginationChange,
  empty,
}) => {
  const ordersT = useTranslation(NSKeys.counterpartyOrders);
  const commonT = useTranslation(NSKeys.common);
  const locale = ordersT.i18n.language;
  const withModules = tab === ALL_TAB;

  const columns: ColumnsType<CounterpartyOrderRow> = [
    {
      title: ordersT.t('columns.num'),
      key: 'num',
      width: 140,
      render: (_, row) => (
        <Flex align="center" gap={8}>
          <Link to={`${COUNTERPARTY_ORDERS_PATH}/${row.uid}`} style={{ fontWeight: 500 }}>
            {row.num}
          </Link>
          {withModules && getRowModules(row).map(({ module }) => <ModuleIcon key={module} module={module} />)}
        </Flex>
      ),
    },
    {
      title: ordersT.t('columns.customer'),
      key: 'customer',
      width: 180,
      render: (_, row) => row.customer.title,
    },
    {
      title: ordersT.t('columns.recipient'),
      key: 'recipient',
      width: 180,
      render: (_, row) => row.recipient_name || '—',
    },
    {
      title: ordersT.t('columns.delivery'),
      key: 'delivery',
      render: (_, row) => <DeliveryAddress city={row.delivery_city} address={row.delivery_address} />,
    },
    {
      title: ordersT.t('columns.createdAt'),
      key: 'createdAt',
      width: 140,
      render: (_, row) => formatDateAndTimeShort(fromUtc(row.created_at)),
    },
    {
      title: ordersT.t('columns.deliveryDate'),
      key: 'deliveryDate',
      width: 140,
      render: (_, row) => formatDate(row.delivery_date),
    },
    {
      title: ordersT.t('columns.status'),
      key: 'status',
      width: 170,
      render: (_, row) =>
        'modules' in row ? <OrderStatusTag name={row.status.name} /> : <OrderStatusTag status={row.status} />,
    },
    {
      title: ordersT.t('columns.places'),
      key: 'places',
      width: 100,
      align: 'right',
      onCell: cargoSpan,
      render: (_, row) =>
        row.cargo_unavailable ? (
          <Typography.Text type="secondary">{ordersT.t('cargoUnavailable')}</Typography.Text>
        ) : (
          <Places places={row.cargo?.places} />
        ),
    },
    {
      title: ordersT.t('columns.weight'),
      key: 'weight',
      width: 100,
      align: 'right',
      onCell: coveredByCargoSpan,
      render: (_, row) => formatDecimal(row.cargo?.weight, locale),
    },
    {
      title: ordersT.t('columns.volume'),
      key: 'volume',
      width: 120,
      align: 'right',
      onCell: coveredByCargoSpan,
      render: (_, row) => formatDecimal(row.cargo?.volume, locale),
    },
  ];

  return (
    <Table<CounterpartyOrderRow>
      rowKey="uid"
      className="tableClassname"
      dataSource={rows}
      columns={columns}
      loading={loading}
      // Ширины заданы, чтобы шапка не прыгала между вкладками: адрес забирает остаток
      tableLayout="fixed"
      scroll={{ x: 1500 }}
      locale={{ emptyText: empty }}
      pagination={{
        current,
        pageSize,
        total,
        showSizeChanger: true,
        position: ['bottomCenter'],
        onChange: onPaginationChange,
        showTotal: (count, range) => `${range[0]}–${range[1]} ${commonT.t('paginationOf')} ${count}`,
      }}
    />
  );
};
